import { get, isBoolean, isEmpty, isNil, isPlainObject } from "lodash";
import cookie from 'react-cookies';
import {BROWSER_KEY, LOCAL_STORAGE_KEY, X_APP_DATA_IGNORE_KEYS} from "./Foundation";
import SameSite from "./SameSite";

export default class StorageHelper {

  static APP_DATA_PAIR_SEPARATOR = ";";
  static APP_DATA_KEY_VALUE_SEPARATOR = "=";

  static isHeaderBasedEnable() {
    return get(window, 'env.headerBasedEnabled');
  }

  static isCookieBasedEnable() {
    return get(window, 'env.cookieBasedEnabled');
  }

  static getArcadiaSuffixCookieName() {
    return `${BROWSER_KEY.IS_ARCADIA}${get(window, "env.arcadiaCookieSuffix", "")}`;
  }

  static getEsportHubSuffixCookieName() {
    return `${BROWSER_KEY.IS_ESPORTSHUB}${get(window, "env.arcadiaCookieSuffix")}`;
  }

  static getXAppDataKey() {
    return [
      BROWSER_KEY.__PREFS,
      BROWSER_KEY._OG,
      BROWSER_KEY.SPECIFY_SPORT,
      BROWSER_KEY.LANG,
      BROWSER_KEY.IS_ARCADIA,
      BROWSER_KEY.IS_ESPORTSHUB,
      BROWSER_KEY._LOGIN_ID,
      BROWSER_KEY.EXTERNAL_URL,
      BROWSER_KEY.DIRECTUS_TOKEN,
      BROWSER_KEY.TRUSTCODE,
      localStorage.getItem("_dp") || '',
      `${this.getEsportHubSuffixCookieName()}`,
      `${this.getArcadiaSuffixCookieName()}`
    ].filter(key => key);
  }

  static convertStringToJSON(text) {
    try {
      if (isEmpty(text)) return {};
      return JSON.parse(text);
    } catch (e) {
      return {};
    }
  }

  /**
   * Gets the subdomain of the current domain.
   *
   * @returns {string} The subdomain of the current domain, e.g. ".example.com".
   */
  static getDomain() {
    let parts = document.domain.split('.');
    let domain = document.domain;
    if (parts.length > 2 && isNaN(parts[0])) {
      parts.splice(0, parts.length - 2);
      domain = "." + parts.join(".");
    }
    return domain;
  }


  static isXAppDataKey(key) {
    return this.getXAppDataKey().includes(key);
  }

  /**
   * Saves a cookie with the given key and value.
   *
   * @param {string} key - The key of the cookie to save.
   * @param {string|null} value - The value of the cookie to save.
   * @param {object} options - Options for the cookie.
   * @param {number|Date} options.expires - The expiration date of the cookie.
   *    Can be a number representing the number of days to expire, or a Date object.
   * @param {string} options.path - The path for which the cookie is valid.
   * @param {string} options.domain - The domain for which the cookie is valid.
   */
  static saveCookie(key, value, options) {
    options = options || {};
    if (value === null) {
      value = '';
      options.expires = -1;
    }
    let expires = '';
    if (options.expires && (typeof options.expires == 'number' || options.expires.toUTCString)) {
      let date = options.expires;
      if (typeof options.expires == 'number') {
        date = new Date();
        date.setTime(date.getTime() + (options.expires * 24 * 60 * 60 * 1000));
      }
      expires = '; expires=' + date.toUTCString(); // use expires attribute, max-age is not supported by IE
    }
    const path = options.path ? '; path=' + (options.path) : '';
    const domain = options.domain ? '; domain=' + (options.domain) : '';
    const sameSite = (window.location.protocol === 'https:' && SameSite.isSameSiteCompatible()) ? "; SameSite=None; Secure" : "";
    document.cookie = [key, '=', encodeURIComponent(value), expires, path, domain, sameSite].join('');
  }

  /**
   * Retrieves data from either localStorage or cookie based on the `isHeaderBasedEnable` flag.
   *
   * If `isHeaderBasedEnable` is true or undefined, retrieves data from localStorage.
   * Otherwise, retrieves data from cookies.
   *
   * @param {string} key The key of the data to retrieve.
   * @returns {string} The retrieved data, or an empty string if not found.
   */
  static getBrowsersStorage(key) {
    const isHeaderBasedEnable = this.isHeaderBasedEnable();
    if ((isHeaderBasedEnable || !isBoolean(isHeaderBasedEnable))) {
      const valueStorage = get(this.convertStringToJSON(localStorage.getItem(LOCAL_STORAGE_KEY.APP_DATA)), key, "");
      const value = this.isXAppDataKey(key) ? decodeURIComponent(valueStorage) : localStorage.getItem(key) || "";
      if (value) return value;
    }
    return cookie.load(key) || "";

  };

  /**
   * Saves data to either localStorage or cookies based on the `isHeaderBasedEnable` flag.
   * 
   * This function determines the storage mechanism as follows:
   *  - If `isHeaderBasedEnable` is true or its type is not a boolean (setting key does not return from service): 
   *    - The data is saved to localStorage unless the key starts with 'x-app-data', in which case it's saved using `this.saveXAppData()`.
   *  - If `isHeaderBasedEnable` is false: 
   *    - The data is saved to cookies with a default path of '/' and a domain derived from `this.getDomain()`.
   * 
   * @param {string} key The key for the data.
   * @param {any} value The data to save. If it's a plain object, it's stringified using JSON.stringify().
   * @param {object} options Additional options for saving the data, such as path and domain (for cookie storage).
   * 
   */
  static saveBrowsersStorage(key, value = '', options = {}) {
    try {
      const isHeaderBasedEnable = this.isHeaderBasedEnable();

      if (isHeaderBasedEnable || !isBoolean(isHeaderBasedEnable)) {
        const valueStorage = isPlainObject(value) ? JSON.stringify(value) : value;
        this.isXAppDataKey(key) ? this.saveXAppData(`${key}=${encodeURIComponent(value)}`) : localStorage.setItem(key, valueStorage);

        if (isHeaderBasedEnable && !this.isCookieBasedEnable()) return;
      }

      this.saveCookie(key, value, {
        path: "/",
        domain: this.getDomain(),
        ...options
      });

    } catch (error) {
      console.error("Can not save data across contexts");
    }
  };

  /**
   * Remove browser data (localStorage or cookie) by key.
   *
   * @param {string} key The key of the data to remove.
   * @param {Object} options Optional options for removing cookie data.
   */
  static removeBrowsersStorage(key, options) {
    const isHeaderBasedEnable = this.isHeaderBasedEnable();
    if (isHeaderBasedEnable || !isBoolean(isHeaderBasedEnable)) {
      this.isXAppDataKey(key) ? this.removeXAppDataByKey(key) : localStorage.removeItem(key);
      if (isHeaderBasedEnable && !this.isCookieBasedEnable()) return;
    }
    this.saveCookie(key, null, {
      path: "/",
      domain: this.getDomain(),
      ...options
    });

  }

  /**
   * Initializes the B2B application data.
   * 
   * @param {string} appData The application data string.
   * @returns {string} The initialized application data string.
   */
  static initB2BXAppData(appData) {
    const xAppData = StorageHelper.convertStringToJSON(appData);
    const result = {};
    
    for (const key in xAppData) {
      if (!isNil(key) && !isNil(xAppData[key])) {
        result[key] = encodeURIComponent(xAppData[key]);
      }
    }

    return JSON.stringify(result);
  };

  /**
   * Returns a string representation of the x-app-data stored in localStorage,
   * formatted as a header string with key-value pairs separated by semicolons.
   *
   * @returns {string} A string representation of the app data in header format.
   */
  static getXAppDataHeader() {
    const appData = StorageHelper.convertStringToJSON(localStorage.getItem(LOCAL_STORAGE_KEY.APP_DATA));

    return Object.entries(appData).reduce((result, entry) => {
      const [key, value] = entry;
      if (!isNil(key) && !isNil(value)) {
        result += `${key}=${value};`;
      }
      return result;
    }, '');

  };

  /**
   * Saves x-app-data to local storage.
   *
   * @param {string} appDataHeader A string containing key-value pairs separated by ';'
   * @param {boolean} isInitial Whether this is the initial app data being saved
   */
  static saveXAppData(appDataHeader, isInitial = false) {
    if (!appDataHeader) return;
    const appDataObj = isInitial ? {} : StorageHelper.convertStringToJSON(localStorage.getItem(LOCAL_STORAGE_KEY.APP_DATA));
    const pairs = appDataHeader.split(this.APP_DATA_PAIR_SEPARATOR);

    const updatedAppData = pairs.reduce((result, pair) => {
      const [key, value] = pair.split(this.APP_DATA_KEY_VALUE_SEPARATOR);
      result[key] = value;
      return result;
    }, {});

    localStorage.setItem(
      LOCAL_STORAGE_KEY.APP_DATA,
      JSON.stringify(this.removeFalsyProperties({ ...appDataObj, ...updatedAppData }))
    );
  };

  static removeXAppDataByKey(key) {
    const xAppData = StorageHelper.convertStringToJSON(localStorage.getItem(LOCAL_STORAGE_KEY.APP_DATA));
    delete xAppData[key];
    localStorage.setItem(LOCAL_STORAGE_KEY.APP_DATA, JSON.stringify(xAppData));
  }

  static removeFalsyProperties(obj = {}) {
    const filteredObj = {};
    for (const key in obj) {
      if (obj[key]) {
        filteredObj[key] = obj[key];
      }
    }
    return filteredObj;
  };

  /**
   * Clears all session data stored in browser (Local Storage and Cookies)
   *
   * This method clears all session data, except for the dynamic parameter key
   * if it's header-based enabled.
   *
   */
  static clearSessionBrowser() {
    const dynamicParameterKey = localStorage.getItem("_dp") || '';
    const isHeaderBasedEnable = this.isHeaderBasedEnable();
    
    // If header-based enabled or the value is not a boolean, proceed with clearing local storage
    if (isHeaderBasedEnable || !isBoolean(isHeaderBasedEnable)) {
      const dynamicParamValue = this.getBrowsersStorage(dynamicParameterKey);
      // Remove token from storage
      this.removeToken();
      // Remove all keys defined in BROWSER_KEY(key use either in cookie and storage) from local storage
      this.removeBrowsersKey();
      // If dynamic parameter value is empty, remove app data from local storage
      // Otherwise, store it in local storage with the dynamic parameter key
      isEmpty(dynamicParamValue) ?
        localStorage.removeItem(LOCAL_STORAGE_KEY.APP_DATA) :
        localStorage.setItem(LOCAL_STORAGE_KEY.APP_DATA, JSON.stringify({ [dynamicParameterKey]: dynamicParamValue }));
      if (isHeaderBasedEnable && !this.isCookieBasedEnable()) return;
    }

    // Otherwise, clear cookies
    const cookies = cookie.loadAll();
    Object.keys(cookies).forEach(cookieName => {
      if (cookieName !== dynamicParameterKey) {
        cookie.remove(cookieName, { path: '/' });
        cookie.remove(cookieName, { path: '/', domain: this.getDomain() });
      }
    });
  }

  static removeBrowsersKey() {
    for (const key in BROWSER_KEY) {
      if (!this.isXAppDataKey(BROWSER_KEY[key])) {
        localStorage.removeItem(BROWSER_KEY[key]);
      }
    }
  }

  static removeToken() {
    localStorage.removeItem(LOCAL_STORAGE_KEY.TOKEN);
  }

  static saveToken(token) {
    if (isEmpty(token)) return;
    localStorage.setItem(LOCAL_STORAGE_KEY.TOKEN, token);
  }

  static removeKeysInXAppData() {
    const dynamicParameterKey = localStorage.getItem("_dp") || '';
    const ignoreKeys = [X_APP_DATA_IGNORE_KEYS, dynamicParameterKey].flat();
    const xAppData = StorageHelper.convertStringToJSON(localStorage.getItem(LOCAL_STORAGE_KEY.APP_DATA));

    Object.keys(xAppData).forEach(key => {
      if (!ignoreKeys.includes(key)) {
        delete xAppData[key];
      }
    });

    localStorage.setItem(LOCAL_STORAGE_KEY.APP_DATA, JSON.stringify(xAppData));
  }

  static saveVHUCode(vhuCode){
    const currentVhcode = localStorage.getItem(LOCAL_STORAGE_KEY.V_HUCODE)
    if(!vhuCode || currentVhcode === vhuCode) return
    localStorage.setItem(LOCAL_STORAGE_KEY.V_HUCODE, vhuCode)
  }

  static getVHCODE(){
    return localStorage.getItem(LOCAL_STORAGE_KEY.V_HUCODE)
  }
}


// WEBPACK FOOTER //
// ./src/app/common/StorageHelper.js